/** Shared simulation enums and small value types. No behaviour lives here. */

/**
 * The belligerents on a field.
 *
 * There used to be three of these and only two of them could fight: everything
 * in the simulation asked `team === Team.Player` or `team === Team.Invader` and
 * meant "mine" and "theirs". That is fine for a siege, which is one town against
 * one horde, and it cannot express a 2v2 at all.
 *
 * So there are four belligerents now, and **hostility is a property of the
 * alliance, never of the pair**. `Team.Ally` is the second banner on the
 * player's side and `Team.Warband` the second on the invader's; `areHostile`
 * and `alliedWith` below are the only two questions anything is allowed to ask.
 * Appended, never inserted, for the reason `RESOURCE_KEYS` appends: the ids are
 * written into every save, every hash and every typed-array column in the game.
 *
 * `Team.Neutral` means exactly what it always meant — a bystander, hostile to
 * nobody and allied to nobody — which is why it keeps its number and gets its
 * own alliance rather than being folded into one of the two sides.
 */
export const Team = {
  Player: 0,
  Invader: 1,
  Neutral: 2,
  /** The second crown on the player's side of a 2v2. AI-steered unless `pvp`. */
  Ally: 3,
  /** The second horde on the invader's side of a 2v2. */
  Warband: 4,
} as const;
export type TeamId = (typeof Team)[keyof typeof Team];

/**
 * The two sides, and nobody's side.
 *
 * A siege is `Home` (the town) against `Foe` (the horde) and always was; what
 * has changed is that the question is asked of the alliance rather than of the
 * team id, so a second banner can be added to either side without every
 * comparison in the simulation having to learn its number.
 */
export const Alliance = {
  /** `Team.Player` and `Team.Ally`. Owns the walls, the buildings and the gates. */
  Home: 0,
  /** `Team.Invader` and `Team.Warband`. */
  Foe: 1,
  /** `Team.Neutral`, and any id nothing has claimed. Fights nobody. */
  None: 2,
} as const;
export type AllianceId = (typeof Alliance)[keyof typeof Alliance];

/**
 * Team id → alliance, as a flat table.
 *
 * Eight entries because `teamMask` in `spatial.ts` packs a team into three bits,
 * so anything reading this must be safe for `team & 7`. The unclaimed ids sit in
 * `None` deliberately: a team nobody has defined should be inert rather than
 * hostile to everything, which is what `a !== b` used to make it.
 *
 * A **static** table rather than a field of `World`, and that is a decision
 * worth defending. Alliances that could be rearranged at runtime would be
 * hashed state, saved state and a migration, and would buy exactly one thing
 * this game does not have: a free-for-all. Two sides is the shape of every mode
 * — siege, field battle, 2v2 — so the mapping is content, and content does not
 * belong in the digest.
 */
export const TEAM_ALLIANCE: readonly number[] = [
  /* 0 Player  */ Alliance.Home,
  /* 1 Invader */ Alliance.Foe,
  /* 2 Neutral */ Alliance.None,
  /* 3 Ally    */ Alliance.Home,
  /* 4 Warband */ Alliance.Foe,
  Alliance.None,
  Alliance.None,
  Alliance.None,
];

/** How many team ids are actually defined. */
export const TEAM_COUNT = 5;

export const TEAM_LABELS: Record<number, string> = {
  [Team.Player]: 'Yours',
  [Team.Invader]: 'The enemy',
  [Team.Neutral]: 'Nobody',
  [Team.Ally]: 'Your ally',
  [Team.Warband]: "The enemy's ally",
};

export function allianceOf(team: number): number {
  return TEAM_ALLIANCE[team & 7];
}

/**
 * Will these two shoot at each other?
 *
 * Symmetric by construction — it compares two alliances and nothing else — and
 * false for anything in `Alliance.None`, which is what makes a bystander a
 * bystander. **This is the predicate**: a bare `a !== b` anywhere in `src/sim`
 * is a 2v2 bug that will not show up until somebody fields four banners.
 */
export function areHostile(a: number, b: number): boolean {
  const x = TEAM_ALLIANCE[a & 7];
  if (x === Alliance.None) return false;
  const y = TEAM_ALLIANCE[b & 7];
  return y !== Alliance.None && x !== y;
}

/**
 * Are these two on the same side?
 *
 * Not the negation of `areHostile`: a neutral is neither hostile to you nor
 * allied with you. Use this one for "does this belong to my side" — a gate that
 * opens, a building that is not a target, a power that does not land on you.
 */
export function alliedWith(a: number, b: number): boolean {
  return TEAM_ALLIANCE[a & 7] === TEAM_ALLIANCE[b & 7];
}

/** Every defined team on the other side of `team`. Empty for a neutral. */
export function hostileTeams(team: number): number[] {
  const out: number[] = [];
  for (let t = 0; t < TEAM_COUNT; t++) if (areHostile(team, t)) out.push(t);
  return out;
}

export const Terrain = {
  AshPlain: 0,
  Ruin: 1,
  Forest: 2,
  StoneOutcrop: 3,
  IronSeam: 4,
  Marsh: 5,
  River: 6,
  Road: 7,
  /** A dug ditch. The cheapest defensive layer there is. */
  Ditch: 8,
  /**
   * A leyline: ground that hums. Ordinary to walk on, buildable, worthless to
   * everyone until the Kindling — and then the only place a Wellspring will
   * stand. It is deliberately *outside* the safe bowl, like every other seam,
   * so reaching the second Age means widening what you have to defend.
   */
  Leyline: 9,
  /**
   * The channel of a real river. Not fordable on foot — this is the tile a
   * bridge exists for.
   *
   * `River` is kept and still means *shallow*: a ford, slow and unpleasant and
   * crossable, which is what every river on every old map was. `Deep` is the
   * middle of a big one. Appended rather than folded into `River` with a depth
   * byte because every table below is keyed by terrain id and a second column
   * would be a second thing to keep in step.
   */
  Deep: 10,
  /** What a player lays across `Deep`. Walkable, and the only way over. */
  Bridge: 11,
} as const;
export type TerrainId = (typeof Terrain)[keyof typeof Terrain];

export const TERRAIN_NAMES: Record<number, string> = {
  [Terrain.AshPlain]: 'Ash Plain',
  [Terrain.Ruin]: 'Ruin',
  [Terrain.Forest]: 'Deadwood',
  [Terrain.StoneOutcrop]: 'Stone Outcrop',
  [Terrain.IronSeam]: 'Iron Seam',
  [Terrain.Marsh]: 'Marsh',
  [Terrain.River]: 'River',
  [Terrain.Road]: 'Old Road',
  [Terrain.Ditch]: 'Ditch',
  [Terrain.Leyline]: 'Leyline',
  [Terrain.Deep]: 'Deep Water',
  [Terrain.Bridge]: 'Bridge',
};

/** Movement cost multiplier per terrain (1 = normal). Infinity = impassable. */
export const TERRAIN_MOVE_COST: Record<number, number> = {
  [Terrain.AshPlain]: 1,
  [Terrain.Ruin]: 1.35,
  [Terrain.Forest]: 1.45,
  [Terrain.StoneOutcrop]: 1.7,
  [Terrain.IronSeam]: 1.2,
  [Terrain.Marsh]: 1.9,
  [Terrain.River]: 3.4,
  [Terrain.Road]: 0.68,
  // Worse than a river to cross and, unlike a river, you get to choose where it
  // is. Steep enough that the flow field will genuinely prefer to go round.
  [Terrain.Ditch]: 3.8,
  // Flat ground that happens to be singing. Nothing about it slows a man down.
  [Terrain.Leyline]: 1,
  /**
   * Deep water is not impassable, and that is deliberate.
   *
   * An impassable channel can cut a map in two, and a flow field that cannot
   * reach the Hearth is a horde that stands still — the worst failure this
   * simulation has. So it is *ruinously* slow instead: a company that wades a
   * big river arrives late, strung out and in range the whole way, which is
   * exactly the defensive value a river is supposed to have. A bridge is the
   * answer to that cost, not to a wall.
   */
  [Terrain.Deep]: 9,
  /** Better than the ground either side of it. That is what a bridge is for. */
  [Terrain.Bridge]: 0.7,
};

/** Terrain that cannot host a building foundation. */
export const TERRAIN_BUILDABLE: Record<number, boolean> = {
  [Terrain.AshPlain]: true,
  [Terrain.Ruin]: true,
  [Terrain.Forest]: true,
  [Terrain.StoneOutcrop]: true,
  [Terrain.IronSeam]: true,
  [Terrain.Marsh]: true,
  [Terrain.River]: false,
  [Terrain.Road]: true,
  [Terrain.Ditch]: false,
  [Terrain.Leyline]: true,
  [Terrain.Deep]: false,
  /**
   * A bridge carries feet, not foundations. Building on one would put a bakery
   * over a river and leave the tile underneath it still deep when it burned.
   */
  [Terrain.Bridge]: false,
};

/**
 * A thing on the battlefield that is not ground, not a building and not a wall.
 *
 * Trees and rocks, scattered from the battle's own seed. They are **state** —
 * hashed inside `BattleState`, saved with it, and rolled off `w.rng` at
 * `createBattle` — because where the cover is decides the whole fight, and a
 * field whose boulders moved on reload is a field two replays disagree about.
 *
 * They are deliberately *not* buildings. A building has health, a team, a
 * footprint on `map.building`, an entry in the store, a repair bill and a place
 * in the district cache; a rock has none of those and never will. They are not
 * walls either: a wall is passable at a price and can be broken through, and the
 * point of a boulder is that it cannot.
 *
 * The tactical half is the occupancy bitmap in `src/sim/obstacles.ts`, which is
 * derived from this list and rebuilt whenever it changes — see
 * `World.obstacleGrid`.
 */
export const ObstacleKind = {
  /** Deadwood. A trunk you cannot walk through, under a canopy you cannot see through. */
  Tree: 0,
  /** A knoll of stone. Solid all the way across, and it stops an arrow dead. */
  Rock: 1,
  /** A thicket: no trunk, all cover. Walkable, and nothing shoots cleanly through it. */
  Thicket: 2,
} as const;
export type ObstacleKindId = (typeof ObstacleKind)[keyof typeof ObstacleKind];

export interface FieldObstacle {
  kind: number;
  /** Centre, in tiles. Whole-tile centres (x.5) so the footprint is symmetric. */
  x: number;
  y: number;
  /** Radius in tiles. The cover reaches this far; the solid core may be smaller. */
  r: number;
}

/**
 * A rise in the ground on a battlefield.
 *
 * State, and hashed inside `BattleState` exactly as `FieldObstacle` is, for
 * exactly the same reason: which side has the hill decides the battle, and a
 * field whose ridge moved on reload is a field two replays disagree about.
 *
 * A **list of discs rather than a map layer**, and the argument is the one
 * `src/sim/obstacles.ts` already made against a fourth terrain array: a layer
 * is `MAP_TILES` bytes in the hash and in every save, for ever, for a thing
 * that exists on a battlefield and nowhere else. A ridge is a chain of
 * overlapping discs and the per-tile height is `World.heightGrid`, which is a
 * pure function of this list — see `src/sim/field.ts`.
 */
export interface FieldHill {
  /** Centre, in tiles. */
  x: number;
  y: number;
  /** Where the rise reaches the surrounding floor, in tiles. */
  r: number;
  /** Height at the crest, in tiles. Hills take the max, never the sum. */
  h: number;
}

/** `World.obstacleGrid` bits. */
/** Feet are refused, arrows stop, and the flow field routes around it. */
export const OB_SOLID = 1;
/** Line of sight is broken and a model standing here is harder to shoot. */
export const OB_COVER = 2;

/**
 * How much of a missile a stand of trees eats, for the man standing in it.
 *
 * Chunky on purpose, like every other multiplier the player is meant to *see*:
 * a company that pulls back into the wood should visibly stop dying to the
 * archers, so that a wood is somewhere you fight from rather than scenery.
 */
export const OBSTACLE_COVER = 0.4;

export const UnitState = {
  Idle: 0,
  Moving: 1,
  Attacking: 2,
  Dying: 3,
  Working: 4,
  Routing: 5,
} as const;
export type UnitStateId = (typeof UnitState)[keyof typeof UnitState];

export const WallKind = {
  None: 0,
  Wall: 1,
  Gate: 2,
  /**
   * A tower: a 2×2 block of these, raised *on* the curtain rather than beside
   * it. Appended rather than inserted, and it lives on the wall layer rather
   * than in the building store on purpose — every existing test in the codebase
   * is either `!== None` or `=== Gate`, so a tower is already a wall to
   * pathing, to solidity, to the flow field and to the save, and none of that
   * needed a line written for it. What it is *not* like a wall in is health,
   * height, and how many men will stand on it: see `tileCapacity` in
   * `systems/curtain.ts`.
   */
  Tower: 3,
} as const;
export type WallKindId = (typeof WallKind)[keyof typeof WallKind];

/** Bit flags on a unit. */
export const UF = {
  Flying: 1 << 0,
  Siege: 1 << 1,
  Shielded: 1 << 2,
  Cavalry: 1 << 3,
  Spear: 1 << 4,
  Ranged: 1 << 5,
  Champion: 1 << 6,
  Undead: 1 << 7,
  Summoned: 1 << 8,
  Sapper: 1 << 9,
  Caster: 1 << 10,
  Villager: 1 << 11,
  Large: 1 << 12,
  SpellEater: 1 << 13,
} as const;

/**
 * What a unit is *made of*, to the ear. Read by the audio layer to pick an
 * impact recipe; derived from flags at `u()` in `data/units.ts` unless a unit
 * authors its own. Lives here rather than in the audio layer because
 * `src/data` may import only this file, and the field belongs on `UnitType`.
 */
export type UnitVoice = 'flesh' | 'bone' | 'metal' | 'beast' | 'timber' | 'spirit';

export const Phase = {
  Build: 0,
  Siege: 1,
  Spoils: 2,
  GameOver: 3,
} as const;
export type PhaseId = (typeof Phase)[keyof typeof Phase];

/**
 * Resources form a two-stage chain, not a flat pile:
 *
 *   Timber ──▶ Planks   (sawmill)    → everything built above a hovel
 *   Grain  ──▶ Bread    (bakery)     → population growth and morale
 *   Stone, Iron                       → walls, arms, engines
 *   Gold                              → wages, mercenaries, trade, tribute
 *   Souls                             → the premium currency, paid in corpses
 *
 * The chain is what turns "collect three numbers" into a settlement with
 * districts: you need the sawmill near the wood, the bakery near the grain, and
 * carts that survive the walk between them.
 *
 * Gold is deliberately the odd one out. Everything else is *produced* by
 * villagers standing next to terrain; gold is *extracted from the people*, by
 * taxing them, and they notice. It is also the only resource that is spent
 * automatically and continuously — soldiers draw a wage every second they are
 * alive. That makes an army a running cost rather than a one-off purchase,
 * which is the whole reason a bigger settlement is worth building.
 *
 * The Kindling adds four more, and the rule they were built under is the one
 * thing worth stating here: **old tiers accumulate**. Magewood is made out of
 * plain timber, runestone out of plain stone, ingots out of plain iron. An
 * Arcane Sawmill does not replace your woodcutters, it gives them somewhere
 * else to send the load — so the second Age makes the first Age's camps *more*
 * valuable, not obsolete. Nothing here supersedes anything above it.
 */
export interface ResourceBundle {
  timber: number;
  planks: number;
  stone: number;
  iron: number;
  grain: number;
  bread: number;
  gold: number;
  souls: number;

  // ── The Kindling ─────────────────────────────────────────────────────────
  /** Ambient, drawn off a leyline by a Wellspring. The input to the other three. */
  essence: number;
  /** Timber that has been talked into being stronger than timber. */
  magewood: number;
  /** Stone cut so it remembers the cut. */
  runestone: number;
  /** Iron rendered down and poured. The only one of the four with no magic in it. */
  ingots: number;

  // ── The Bell (Age 3) ─────────────────────────────────────────────────────
  //
  // The Age the settlement starts spending the dead. Three of the four eat a
  // Kindling good and the fourth eats two, and every one of them wants Souls on
  // top — which is the tier's whole character: the Bell is where the graveyard
  // stops being a place you keep and becomes a place you draw on.
  /** Magewood grafted onto something that was recently a person. */
  soulwood: number;
  /** Runestone fired until the pattern goes out of it. Heavier than it looks. */
  voidstone: number;
  /** Ingots and runestone, poured together. Takes an edge nothing else will hold. */
  alloy: number;
  /** Essence distilled past the point where it is still ambient. */
  quintessence: number;

  // ── The Gilded (Age 4) ───────────────────────────────────────────────────
  //
  // Two goods, not four. By the fourth Age the settlement is running three
  // generations of production at once and the constraint is ground, not recipes;
  // a fifth converter would be another building to fit rather than another
  // decision to make.
  /** Voidstone and quintessence, drawn into sheet. It does not break; it stops. */
  ashglass: number;
  /** Alloy with something alive folded into it. It is warm, and nobody likes it. */
  heartiron: number;

  // ── The Hollow (Age 5) ───────────────────────────────────────────────────
  //
  // The last two, and the only two in the game that cost people. See the note on
  // `BuildingType.toll` in `data/buildings.ts` for what that means at the yard.
  /** Ashglass with the light taken out of it. Cheaper to look through than at. */
  hollowglass: number;
  /** Heartiron rendered down and rung. It keeps the note for about a day. */
  gravesilver: number;

  // ── Race currencies (not Age currencies) ─────────────────────────────────
  //
  // The first three keys in this list that are gated on *who you are* rather
  // than on *how far you have climbed*, and the distinction matters more than
  // it looks. Every key above accumulates: an Age-5 settlement holds all
  // twenty, and the twenty are the same twenty for everybody. These three are
  // held by some peoples and by no others, for the whole of a run, and
  // `raceEconomyMask` in `data/races.ts` is the whole of the gate.
  //
  // Why they are appended to the shared list rather than kept in a per-race
  // one: the hash walks this order, the storage clamp walks it every tick, the
  // save writes it verbatim and `canAfford`/`spend` iterate it. A per-race key
  // list means a per-race hash order and a per-race migration, which is three
  // of the four things parallel work in this repository has already broken
  // once. A masked key simply sits at zero for anyone who cannot hold it —
  // nothing they own produces it, the market refuses it and the clamp pins it —
  // so the digest of a settlement that has never heard of Ichor is exactly what
  // it was before this existed, and `SAVE_VERSION` does not move.
  //
  /** Blood kept warm. The Drowned Court's and the Crimson Assize's, nobody else's. */
  ichor: number;
  /**
   * Karrun-Dûr's, and the only currency in the game you cannot mine, farm, buy
   * or trade. It is written when you *lose* something, and the Reckoning is the
   * only thing that spends it. Uncapped, because a ledger is not a shed.
   */
  grudges: number;
  /** Somebody else's, once. Both goblin peoples and the Moonbound. */
  scrap: number;

  // ── The other two foods ──────────────────────────────────────────────────
  //
  // Appended after the race currencies for the same reason everything is
  // appended: the hash walks this order and the save writes it verbatim. They
  // are **not** Age goods and not race goods — they are two more ways to fill
  // the same appetite `grain` and `bread` fill, gated on the ground rather than
  // on the ladder, and `FOOD_VALUE` in `sim/constants.ts` is the whole of what
  // makes them food.
  /** Netted off a bank. The one food the map hands you rather than the fields. */
  fish: number;
  /** Hunted, salted, hung. Worth twice its weight in grain and half the trouble. */
  meat: number;
}

export type ResourceKey = keyof ResourceBundle;

/**
 * Iteration order for everything that walks the resource list — and there are a
 * dozen such places: the hash, the storage clamp, `canAfford`, `spend`, the
 * repair bill, the save. New Ages **append**; nothing is ever inserted into the
 * middle, because the HUD strip reads in this order and a player who has
 * learned where timber sits should not have to find it again.
 */
export const RESOURCE_KEYS: readonly ResourceKey[] = [
  'timber',
  'planks',
  'stone',
  'iron',
  'grain',
  'bread',
  'gold',
  'souls',
  'essence',
  'magewood',
  'runestone',
  'ingots',
  'soulwood',
  'voidstone',
  'alloy',
  'quintessence',
  'ashglass',
  'heartiron',
  'hollowglass',
  'gravesilver',
  // Race currencies. Appended, never inserted, exactly like every Age before
  // them; the difference is that these are gated by `raceEconomyMask` rather
  // than by `w.age`, and most settlements will hold none of them ever.
  'ichor',
  'grudges',
  'scrap',
  // The other two foods. Appended, never inserted; see `ResourceBundle`.
  'fish',
  'meat',
];

/**
 * Resources the settlement's warehouses cannot hold.
 *
 * Coin and Souls, and deliberately nothing else. The four goods of the Kindling
 * are all **capped**, each for the same reason: they are things in a yard. A
 * bar of ingots takes up as much shed as the iron it came from, magewood is
 * still lumber, and runestone is still stone. Essence was the only real
 * argument — it is not a solid — but capping it is what makes a Wellspring
 * worth *placing near its consumers* rather than left to fill a bottomless well
 * on the far side of the map, and an uncapped input to three converters would
 * let a player bank a whole Age of production during Age 1's quiet waves. So:
 * capped, all four, and storage is a real Age-2 decision.
 *
 * The eight goods of the Bell, the Gilded and the Hollow are capped for the same
 * reason and one more. Every one of them is made at a *rate* — a fully upgraded
 * Ashworks turns out about five sheets a minute — and the prices of the upper
 * Ages are three figures, so the interesting question through the back half of a
 * run is not "can I make this" but "have I anywhere to put it while I make the
 * rest". Uncapping the top of the ladder would delete that question and turn the
 * Vault into decoration. Coin and Souls stay the only two exceptions they have
 * always been.
 */
export const UNCAPPED_RESOURCES: readonly ResourceKey[] = ['gold', 'souls', 'grudges'];

export const RESOURCE_LABELS: Record<ResourceKey, string> = {
  timber: 'Timber',
  planks: 'Planks',
  stone: 'Stone',
  iron: 'Iron',
  grain: 'Grain',
  bread: 'Bread',
  gold: 'Gold',
  souls: 'Souls',
  essence: 'Essence',
  magewood: 'Magewood',
  runestone: 'Runestone',
  ingots: 'Ingots',
  soulwood: 'Soulwood',
  voidstone: 'Voidstone',
  alloy: 'Alloy',
  quintessence: 'Quintessence',
  ashglass: 'Ashglass',
  heartiron: 'Heartiron',
  hollowglass: 'Hollowglass',
  gravesilver: 'Gravesilver',
  ichor: 'Ichor',
  grudges: 'Grudges',
  scrap: 'Scrap',
  fish: 'Fish',
  meat: 'Salt Meat',
};

/** Single-glyph marks for the compact HUD readout. */
export const RESOURCE_GLYPHS: Record<ResourceKey, string> = {
  timber: '⸙',
  planks: '≡',
  stone: '⬟',
  iron: '⚒',
  grain: '☘',
  bread: '◔',
  gold: '⊙',
  souls: '☠',
  essence: '✧',
  magewood: '⋔',
  runestone: '⌘',
  ingots: '▬',
  soulwood: '⋎',
  voidstone: '◈',
  alloy: '⬢',
  quintessence: '✦',
  ashglass: '◇',
  heartiron: '❖',
  hollowglass: '○',
  gravesilver: '☾',
  ichor: '❥',
  grudges: '✎',
  scrap: '⌗',
  fish: '⋉',
  meat: '⊐',
};

export function emptyResources(): ResourceBundle {
  return {
    timber: 0,
    planks: 0,
    stone: 0,
    iron: 0,
    grain: 0,
    bread: 0,
    gold: 0,
    souls: 0,
    essence: 0,
    magewood: 0,
    runestone: 0,
    ingots: 0,
    soulwood: 0,
    voidstone: 0,
    alloy: 0,
    quintessence: 0,
    ashglass: 0,
    heartiron: 0,
    hollowglass: 0,
    gravesilver: 0,
    ichor: 0,
    grudges: 0,
    scrap: 0,
    fish: 0,
    meat: 0,
  };
}

export function cloneResources(r: ResourceBundle): ResourceBundle {
  return {
    timber: r.timber,
    planks: r.planks,
    stone: r.stone,
    iron: r.iron,
    grain: r.grain,
    bread: r.bread,
    gold: r.gold,
    souls: r.souls,
    essence: r.essence,
    magewood: r.magewood,
    runestone: r.runestone,
    ingots: r.ingots,
    soulwood: r.soulwood,
    voidstone: r.voidstone,
    alloy: r.alloy,
    quintessence: r.quintessence,
    ashglass: r.ashglass,
    heartiron: r.heartiron,
    hollowglass: r.hollowglass,
    gravesilver: r.gravesilver,
    ichor: r.ichor,
    grudges: r.grudges,
    scrap: r.scrap,
    fish: r.fish,
    meat: r.meat,
  };
}

/**
 * Tax policy, Stronghold-style: the rate is a dial the player holds, and every
 * notch of extra revenue is bought with the settlement's goodwill. Generosity
 * *costs* gold and buys morale back, which is what makes a bad harvest
 * survivable and a long siege expensive.
 */
export const TaxPolicy = {
  Generous: 0,
  Fair: 1,
  Modest: 2,
  Hard: 3,
  Ruinous: 4,
} as const;
export type TaxPolicyId = (typeof TaxPolicy)[keyof typeof TaxPolicy];

export interface TaxBand {
  name: string;
  /** Gold per head per minute. Negative means the treasury pays the people. */
  goldPerHeadPerMin: number;
  /** Added to the settlement's morale target. */
  morale: number;
  note: string;
}

export const TAX_BANDS: readonly TaxBand[] = [
  {
    name: 'Generous',
    goldPerHeadPerMin: -0.18,
    morale: 0.22,
    note: 'You pay them. They will forgive a great deal for it.',
  },
  { name: 'Fair', goldPerHeadPerMin: 0, morale: 0.08, note: 'No tithe. No complaint.' },
  { name: 'Modest', goldPerHeadPerMin: 0.22, morale: -0.06, note: 'A tithe they can live with.' },
  { name: 'Hard', goldPerHeadPerMin: 0.46, morale: -0.2, note: 'They can live with it. They will not like you.' },
  {
    name: 'Ruinous',
    goldPerHeadPerMin: 0.78,
    morale: -0.42,
    note: 'Take everything. Some of them will leave. Some will do worse.',
  },
];

export function taxBand(policy: number): TaxBand {
  return TAX_BANDS[Math.max(0, Math.min(TAX_BANDS.length - 1, policy | 0))];
}

export function canAfford(have: ResourceBundle, cost: Partial<ResourceBundle>): boolean {
  for (const k of RESOURCE_KEYS) {
    const need = cost[k] ?? 0;
    if (need > 0 && have[k] < need) return false;
  }
  return true;
}

export function spend(have: ResourceBundle, cost: Partial<ResourceBundle>): void {
  for (const k of RESOURCE_KEYS) {
    const need = cost[k] ?? 0;
    if (need !== 0) have[k] -= need;
  }
}

export function addResources(have: ResourceBundle, gain: Partial<ResourceBundle>, scale = 1): void {
  for (const k of RESOURCE_KEYS) {
    const g = gain[k] ?? 0;
    if (g !== 0) have[k] += g * scale;
  }
}

/** Job slots villagers can be assigned to. */
export const Job = {
  Idle: 0,
  Woodcutter: 1,
  Quarrier: 2,
  Miner: 3,
  Farmer: 4,
  Smith: 5,
  Acolyte: 6,
  Builder: 7,
  Sawyer: 8,
  Baker: 9,
  Carter: 10,
  /** A weir on a bank. Appended, like every id written into a typed column. */
  Fisher: 11,
  Hunter: 12,
} as const;
export type JobId = (typeof Job)[keyof typeof Job];

export const JOB_LABELS: Record<number, string> = {
  [Job.Idle]: 'Idle',
  [Job.Woodcutter]: 'Woodcutter',
  [Job.Quarrier]: 'Quarrier',
  [Job.Miner]: 'Miner',
  [Job.Farmer]: 'Farmer',
  [Job.Smith]: 'Smith',
  [Job.Acolyte]: 'Acolyte',
  [Job.Builder]: 'Builder',
  [Job.Sawyer]: 'Sawyer',
  [Job.Baker]: 'Baker',
  [Job.Carter]: 'Carter',
  [Job.Fisher]: 'Fisher',
  [Job.Hunter]: 'Hunter',
};

/** Damage classes drive the rock-paper-scissors table. */
export const DamageType = {
  Slash: 0,
  Pierce: 1,
  Blunt: 2,
  Arrow: 3,
  Siege: 4,
  Arcane: 5,
  Fire: 6,
  Poison: 7,
} as const;
export type DamageTypeId = (typeof DamageType)[keyof typeof DamageType];

/** Armour classes. */
export const ArmourType = {
  Unarmoured: 0,
  Leather: 1,
  Mail: 2,
  Plate: 3,
  Shielded: 4,
  Structure: 5,
  Monstrous: 6,
} as const;
export type ArmourTypeId = (typeof ArmourType)[keyof typeof ArmourType];

/**
 * Multiplier table [damageType][armourType]. This is the readable
 * rock-paper-scissors the player is supposed to *see* working.
 */
export const DAMAGE_TABLE: number[][] = [
  //          unarm  leather mail   plate  shield struct monstrous
  /* slash */ [1.25, 1.15, 0.9, 0.65, 0.75, 0.35, 1.0],
  /* pierce*/ [1.0, 1.05, 1.15, 1.35, 0.7, 0.4, 1.3],
  /* blunt */ [0.95, 1.0, 1.2, 1.25, 1.35, 0.85, 1.1],
  /* arrow */ [1.35, 1.2, 0.85, 0.55, 0.35, 0.3, 1.0],
  /* siege */ [0.8, 0.85, 0.95, 1.05, 1.0, 2.6, 1.45],
  /* arcane*/ [1.1, 1.1, 1.1, 1.1, 1.1, 0.7, 1.0],
  /* fire  */ [1.2, 1.3, 1.0, 0.85, 0.9, 1.25, 0.9],
  /* poison*/ [1.4, 1.25, 0.9, 0.6, 0.9, 0.05, 0.75],
];

export function damageMultiplier(dmg: number, armour: number): number {
  const row = DAMAGE_TABLE[dmg];
  if (!row) return 1;
  const v = row[armour];
  return v === undefined ? 1 : v;
}

export const DAMAGE_LABELS: Record<number, string> = {
  [DamageType.Slash]: 'Slash',
  [DamageType.Pierce]: 'Pierce',
  [DamageType.Blunt]: 'Blunt',
  [DamageType.Arrow]: 'Arrow',
  [DamageType.Siege]: 'Siege',
  [DamageType.Arcane]: 'Arcane',
  [DamageType.Fire]: 'Fire',
  [DamageType.Poison]: 'Poison',
};

export const ARMOUR_LABELS: Record<number, string> = {
  [ArmourType.Unarmoured]: 'Unarmoured',
  [ArmourType.Leather]: 'Leather',
  [ArmourType.Mail]: 'Mail',
  [ArmourType.Plate]: 'Plate',
  [ArmourType.Shielded]: 'Shielded',
  [ArmourType.Structure]: 'Structure',
  [ArmourType.Monstrous]: 'Monstrous',
};
